import styles from '../ResourcesPages.module.css';
import FEATURE_DEFINITIONS from "../../../Data/FeatureDefinitions.json";
import { useEffect, useState, useContext } from 'react';
import { LanguageContext } from '../../../../Context/Language.Context';
import { usePopUpAnimation } from '../../../../Hooks/AnimationHook/UseAnimiationHook';
import { useFeatureDefinitionsHook } from '../../../../Hooks/use-context-hooks';

import featureDefintionsBackgroundImage from '../../../Theme/Product/CategoryImages/kitchen-3.webp';
import { motion } from 'framer-motion';

import HeroContainer from '../../../Components/UI/HeroContainer/HeroContainer';
import SectionTextContainer from '../../../Components/UI/SectionTextContainer/SectionTextContainer';
import TheIcon from '../../../Components/UI/TheIcon/TheIcon';
import { GridSystem } from '../../../Components/GridSystem/GridSystem';

import {   
  printFeaturesByProductType,
  getFeatureAuditList,
  getSharedFeatureDefinitionsTableRows
 } from './DefinitionsPlayground';

const getLangKey = (language) => {
  return language === 'FR' ? 'fr' : 'en';
};

const getPageTitle = (language) => {
  return language === 'EN'
    ? 'Feature Definitions'
    : 'Définitions des caractéristiques';
};

const getProductType = (definition, language) => {
  const langKey = getLangKey(language);

  return (
    definition.productType?.[langKey] ||
    definition.productType?.en ||
    definition[language === 'EN' ? 'product-type-en' : 'product-type-fr'] ||
    definition['product-type-en'] ||
    'Other'
  );
};

const getFeatureTitle = (definition, language) => {
  const langKey = getLangKey(language);

  return (
    definition.feature?.[langKey] ||
    definition.feature?.en ||
    definition[language === 'EN' ? 'feature-en' : 'feature-fr'] ||
    definition['feature-en'] ||
    ''
  );
};

const getFeatureDefinition = (definition, language) => {
  const langKey = getLangKey(language);

  return (
    definition.definition?.[langKey] ||
    definition.definition?.en ||
    definition[language === 'EN' ? 'definition-en' : 'definition-fr'] ||
    definition['definition-en'] ||
    ''
  );
};

const groupByProductType = (definitions, language) => {
  return definitions.reduce((acc, curr) => {
    const productType = getProductType(curr, language);

    if (!acc[productType]) {
      acc[productType] = [];
    }

    acc[productType].push(curr);
    return acc;
  }, {});
};

const createInitialState = (groupedData) => {
  const initialState = {};

  for (const key in groupedData) {
    initialState[key] = false;
  }

  return initialState;
};

const DefinitionsPage = () => {
  const animationProps = usePopUpAnimation();
  const { language, translatedText } = useContext(LanguageContext);
  const { definitions, isLoading, error } = useFeatureDefinitionsHook();

  const [showSection, setShowSection] = useState({});
  const [groupedData, setGroupedData] = useState({});

  const isUsingStaticData =
    !!error || !Array.isArray(definitions) || definitions.length === 0;

  const resolvedDefinitions = isUsingStaticData
    ? FEATURE_DEFINITIONS
    : definitions;

  const toggleDefinition = (definition) => {
    setShowSection((prevState) => ({
      ...prevState,
      [definition]: !prevState[definition],
    }));
  };

  useEffect(() => {
    if (!Array.isArray(resolvedDefinitions)) return;

    const grouped = groupByProductType(resolvedDefinitions, language);

    setGroupedData(grouped);
    setShowSection(createInitialState(grouped));
  }, [resolvedDefinitions, language]);

  console.table(
  getFeatureAuditList().flatMap((group) =>
    group.features.map((feature) => ({
      productType: group.productTypeEn,
      featureEn: feature.en,
      featureFr: feature.fr,
    }))
  )
);

console.table(getSharedFeatureDefinitionsTableRows(resolvedDefinitions));
  return (
    <>
      <HeroContainer
        backgroundImage={featureDefintionsBackgroundImage}
        title={getPageTitle(language)}
      />

      <GridSystem containerPaddingBottom="2rem">
        <div className={styles.contentWrapper}>
          <SectionTextContainer
            subtitleText={translatedText.resources}
            titleText={translatedText.exFeatureDefinitions}
            descriptionText={translatedText.describeFeatureDefinitions}
          />
        </div>
      </GridSystem>

      <GridSystem containerPaddingBottom="4rem">
        <div className={styles.contentWrapper}>
          {isLoading && !isUsingStaticData && (
            <p>Loading feature definitions...</p>
          )}

         

          <section className={styles.definitionsWrapper}>
            {Object.entries(groupedData).map(([productType, definitions], index) => (
              <div key={productType || index} className={styles.mainContentWrapper}>
                <div
                  className={styles.productTypeHeader}
                  onClick={() => toggleDefinition(productType)}
                >
                  <motion.p
                    {...animationProps}
                    className={styles.productTypeText}
                    id={productType}
                  >
                    {productType}
                  </motion.p>

                  {showSection[productType] !== true ? (
                    <TheIcon name="expand" />
                  ) : (
                    <TheIcon name="close" />
                  )}
                </div>

                {showSection[productType] && (
                  <motion.main
                    {...animationProps}
                    className={styles.featureDefinitionsWrapper}
                  >
                    {definitions.map((definition, idx) => (
                      <div
                        key={definition._id || definition.slug || idx}
                        className={styles.featureDefinitionsRow}
                      >
                        <p
                          onClick={() => toggleDefinition(productType)}
                          className={styles.featureTitleText}
                        >
                          {getFeatureTitle(definition, language)}
                        </p>

                        <div>
                          <p className={styles.definitionText}>
                            {getFeatureDefinition(definition, language)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </motion.main>
                )}
              </div>
            ))}
          </section>
        </div>
         {isUsingStaticData && (
            <p style={{ paddingTop:'3rem'}} className={styles.definitionText}>
              Live feature definition data is currently unavailable. Static feature definitions are being shown.
            </p>
          )}
      </GridSystem>
    </>
  );
};

export default DefinitionsPage;